<template>
  <v-flex :class="$style.currentOrderItem"
          xs12>
    <div :class="$style.orderItemTitle">

      <div :class="$style.orderItemName">
        <h6>{{ name }}</h6>
        <a v-if="!disabled"
           :class="$style.editBtn"
           @click="handleEdit">
          <svg v-svg
               :class="$style.editIcon"
               symbol="edit-icon"/>
        </a>
      </div>
      <QuantityPicker :quantity="quantity"
                      :disabled="disabled"
                      @change-quantity="handleChangeQuantity"/>
    </div>

    <div :class="$style.orderItemDescription">
      <span>{{ stringifyItems(items) }}</span>
    </div>

    <div
      v-if="bossBundle && doesOrderIncludeBundle(orderItem.data)"
      :class="$style.orderItemBundle"
    >
      <template v-if="doesOrderIncludePartialBundle(orderItem.data)">
        <div :class="$style.orderItemName">
          <h6>Fries</h6>
        </div>
        <div :class="$style.orderItemDescription">
          {{ stringifyItems(bundleItems) }}
        </div>
      </template>
      <template v-else>
        <div :class="$style.orderItemName">
          <h6>{{ bossBundle.name }}</h6>
        </div>
        <div :class="$style.orderItemDescription">
          <span>{{ stringifyItems(bundleItems) }}</span>
        </div>
      </template>
    </div>
    <p v-if="isOrderItemIncompleted"
       :class="$style.incompleteItemBadge">
      <v-icon color="orange">error_outline</v-icon>
      Configuration is not completed
    </p>

    <div :class="$style.orderItemTotal">
      <h6>Item Total</h6>
      <span>{{ price | toPrice }}</span>
    </div>
  </v-flex>
</template>

<script>
import Component from 'vue-class-component';
import Vue from 'vue';
import QuantityPicker from '@/components/QuantityPicker';
import { Getter } from 'vuex-class';
import { CategoryWizard } from '@/apps/menu/constants';

/**
 * Renders product data inside cart component.
 *
 * PROPS
 * ============================================================================
 * @prop {Object} product - product data to render.
 * @prop {Boolean} disabled - Change of product and the number of products blocked.
 */
@Component({
  name:       'CartProduct',
  components: {
    QuantityPicker,
  },
  props: {
    orderItemId: {},
    disabled:    {
      type:     Boolean,
      required: false,
      default:  false,
    },
  },
})
export default class CartProduct extends Vue {
  @Getter productByKey;
  @Getter itemByKey;
  @Getter modifierByKey;
  @Getter cart;
  @Getter orderItemPrice;
  @Getter categories;
  @Getter products;
  @Getter getMainItemsFromOrderItem;
  @Getter getBundleItemsFromOrderItem;
  @Getter doesOrderIncludeBundle;
  @Getter doesOrderIncludePartialBundle;
  /** Current order item that is being configured in the cart. */
  @Getter('orderItemId') currentOrderItemId;
  @Getter orderItemCompleted;

  /**
   * Retrieves order item from cart store.
   * @returns {*}
   */
  get orderItem() {
    return this.cart[this.orderItemId];
  }

  /**
   * Retrieves product data from menu
   * @returns {*}
   */
  get product() {
    return this.productByKey(this.orderItem.productKey);
  }

  /**
   * Retrieve product name for order item
   * @returns {string}
   */
  get name() {
    return this.product.name;
  }

  /**
   * Retrieve quantity of order item
   * @returns {number}
   */
  get quantity() {
    return this.orderItem.quantity || 1;
  }

  /**
   * Retrieve formatted list of items from order item
   * @returns {Array}
   */
  get items() {
    const items = this.getMainItemsFromOrderItem(this.orderItem.data);

    if (!this.bossBundle) {
      return [
        ...items,
        ...this.getBundleItemsFromOrderItem(this.orderItem.data),
      ];
    }

    return items;
  }

  /**
   * Get boss bundle product
   * @returns {Object}
   */
  get bossBundle() {
    let bossBundleSubId;

    this.product.categories.forEach((categoryId) => {
      if (
        this.categories[categoryId].wizardType === CategoryWizard.CHILD_WIZARD
      ) {
        bossBundleSubId = this.categories[categoryId].subProductId;
      }
    });

    return bossBundleSubId ? this.products[bossBundleSubId] : null;
  }


  /**
   * Get boss bundle items
   * @returns {string}
   */
  get bundleItems() {
    return this.getBundleItemsFromOrderItem(this.orderItem.data);
  }


  stringifyItems(items) {
    return items.map(({ quantity, modifier, name }) => {
      const countPostfix = (quantity > 1) ? `*${quantity}` : '';
      const modifierName = this.modifierByKey(modifier) ? ` ${this.modifierByKey(modifier).name}` : '';

      return `${name}${modifierName}${countPostfix}`;
    }).join(', ');
  }

  /**
   * Calculate price for item order based on item prices
   * @returns {number}
   */
  get price() {
    const { quantity } = this.cart[this.orderItemId];

    return this.orderItemPrice(this.orderItemId) * quantity;
  }

  handleChangeQuantity(quantity) {
    if (this.disabled) {
      return;
    }
    this.$emit('change-quantity', quantity);
  }

  handleEdit() {
    if (this.disabled) {
      return;
    }
    this.$emit('edit', this.orderItemId);
  }

  get isOrderItemIncompleted() {
    return this.orderItemId === this.currentOrderItemId && !this.orderItemCompleted;
  }
}
</script>

<style lang="scss" module>
@import "~@/style/variables.scss";

.editIcon {
  width: 14px;
  height: 14px;
}

.currentOrderItem {
  background-color: $white;
  margin-top: 4px;
  margin-bottom: 2px;
  box-shadow: 0 1px 2px 0 rgba(125, 125, 125, 0.37);
  --border: 1px dashed #DEDEDE;
}

.orderItemTitle {
  display: flex;
  justify-content: space-between;
  padding: 12px 16px 10px 22px;
}

.orderItemName {
  display: flex;

  h6 {
    margin-right: 6px;
  }
}

.orderItemDescription {
  padding-left: 22px;
  padding-right: 19px;
  font-family: 'Gotham', sans-serif;
  font-size: 12px;
  font-weight: 300;
  font-style: italic;
  color: $black-semi-transparent;
  margin-bottom: 11px;
}

.orderItemBundle {
  padding-bottom: 8px;
  border-bottom: var(--border);

  .orderItemName {
    padding-left: 22px;
    padding-right: 19px;
    padding-bottom: 9px;
  }
}

.orderItemTotal {
  padding: 9px 15px 16px 23px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: 'Gotham', sans-serif;
  font-size: 15px;
  font-weight: 500;
}

.incompleteItemBadge {
  --warn-color: orange;
  color: var(--warn-color);
  padding: 10px 20px;
  font-weight: bold;
  border-bottom: var(--border);
  display: flex;
  gap: 10px;
  align-items: center;
}

@media screen and (max-width: $kiosk-breakpoint-max) {
  .editIcon {
    width: 40px;
    height: 40px;
  }
  .currentOrderItem {
    box-shadow: 0 1px 5px 0 rgba(139, 139, 139, 0.81);
    margin-bottom: 8px;
  }
  .orderItemTitle {
    padding: 36px 22px 10px 45px;
  }
  .orderItemName h6 {
    font-size: 34px;
    line-height: 48px;
  }
  .orderItemDescription {
    padding-left: 45px;
    padding-right: 178px;
    font-size: 26px;
    line-height: 33px;
    margin-bottom: 24px;
  }
  .orderItemBundle {
    padding-bottom: 31px;
    border-bottom: 2px dashed #DEDEDE;

    .orderItemName {
      padding-left: 45px;
      padding-right: 178px;
    }
  }
  .orderItemTotal {
    padding: 20px 45px;
    font-size: 34px;
    line-height: 48px;
    font-weight: 700;

    h6 {
      font-size: 34px;
      letter-spacing: 0.19px;
      line-height: 48px;
    }
  }
}

@media screen and (max-width: $mobile-breakpoint-max) {
  .editIcon {
    width: 14px;
    height: 14px;
  }

  .currentOrderItem {
    box-shadow: 0 1px 2px 0 rgba(125, 125, 125, 0.37);
    margin: 0;

    &+& {
      margin-top: 5px;
    }
  }

  .orderItemTitle {
    padding: 12px 16px 4px 22px;
  }

  .orderItemName {
    h6 {
      font-size: 15px;
      line-height: 20px;
    }
  }

  .orderItemDescription {
    padding-left: 22px;
    padding-right: 19px;
    font-size: 12px;
    line-height: 14px;
    margin-bottom: 11px;
  }

  .orderItemBundle {
    padding-bottom: 8px;
    border-bottom: var(--border);

    .orderItemName {
      padding-left: 22px;
      padding-right: 19px;
      padding-bottom: 9px;
    }
  }

  .orderItemTotal {
    padding: 9px 15px 16px 23px;
    font-size: 15px;
    line-height: 20px;
    font-weight: 500;

    h6 {
      font-size: 15px;
      font-weight: 700;
      letter-spacing: -0.21px;
      line-height: 20px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/apps/cart/components/CartProduct.vue